import {
  ARS,
  ArsContextProvider,
  ArsContextState,
  CURRENT_HIERARCHY_ID,
  datasetToAnnotatedVariables,
  EditorDefns,
} from "aic";
import { getArsSaveBlockers } from "contexts/appsettings/saveBlockers/getArsSaveBlockers";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { getClinicalLibraryData } from "contexts/hierarchy/getClinicalLibraryData";
import {
  HierarchyArsObjects,
  HierarchyParentTemplates,
  UPDATE_HIERARCHY,
} from "contexts/hierarchy/interface";
import { eApplicationPermission, eTlfItemType } from "functions/api/enums";
import { getCaseWhenConditionsFromTlf } from "./getCaseWhenConditionsFromTlf";

/**
 * Component to interface between the ARS context and hierarchy context.
 *
 * Builds hierarchy payload data from ARS context state, applies any parent templates in the appropriate place
 *
 * @param state - ARS context state containing the collections to be included in the hierarchy payload.
 * @param currentHierarchyId - The ID of the current hierarchy to filter out its own items from the ARS collections.
 * @returns Hierarchy payload containing ARS collections.
 */
const getHierarchyPayload = <T,>(
  state: ArsContextState<T>,
  currentHierarchyId: string,
): HierarchyArsObjects => ({
  analysisMethods: Object.values(state.analysisMethods)
    .filter(
      (am) =>
        am.fromHierarchyId === currentHierarchyId || am.fromHierarchyId === CURRENT_HIERARCHY_ID,
    )
    .map((am) => {
      const { fromHierarchyId: _1, disableEditing: _2, ...rest } = am;
      return rest;
    }),
  analysisSets: Object.values(state.analysisSets)
    .filter(
      (as) =>
        as.fromHierarchyId === currentHierarchyId || as.fromHierarchyId === CURRENT_HIERARCHY_ID,
    )
    .map((as) => {
      const { fromHierarchyId: _1, disableEditing: _2, ...rest } = as;
      return rest;
    }),
  dataSubsets: Object.values(state.dataSubsets)
    .filter(
      (ds) =>
        ds.fromHierarchyId === currentHierarchyId || ds.fromHierarchyId === CURRENT_HIERARCHY_ID,
    )
    .map((ds) => {
      const { fromHierarchyId: _1, disableEditing: _2, ...rest } = ds;
      return rest;
    }),
  groupingFactors: Object.values(state.groupingFactors)
    .filter(
      (gf) =>
        gf.fromHierarchyId === currentHierarchyId || gf.fromHierarchyId === CURRENT_HIERARCHY_ID,
    )
    .map((gf) => {
      const { fromHierarchyId: _1, disableEditing: _2, ...rest } = gf;
      return rest;
    }),
});

/**
 * Builds hierarchy payload data from hierarchy values.  Ensures parents can be removed
 *
 * @param hierarchy - Current hierarchy.
 * @param hierarchy.id - The ID of the current hierarchy.
 * @param hierarchy.analysisMethods - Analysis methods associated with the hierarchy.
 * @param hierarchy.analysisSets - Analysis sets associated with the hierarchy.
 * @param hierarchy.dataSubsets - Data subsets associated with the hierarchy.
 * @param hierarchy.groupingFactors - Grouping factors associated with the hierarchy.
 * @param parentTemplates - Parent templates of the hierarchy.
 * @returns Hierarchy payload containing ARS collections.
 */
const getHierarchyPayloadFromHierarchy = (
  hierarchy: {
    id?: string;
    analysisMethods?: ARS.AnalysisMethod[];
    analysisSets?: ARS.AnalysisSet[];
    dataSubsets?: ARS.DataSubset[];
    groupingFactors?: ARS.GroupingFactor[];
  },
  parentTemplates?: HierarchyParentTemplates,
): HierarchyArsObjects => ({
  analysisMethods: [
    ...(hierarchy?.analysisMethods ?? []).map((am) => ({
      ...am,
      fromHierarchyId: hierarchy?.id,
    })),
    ...(parentTemplates?.analysisMethods ?? [])
      .filter((pam) => !hierarchy?.analysisMethods?.some((am) => am.name === pam.name))
      .map((pam) => ({
        ...pam,
        disableEditing: true,
      })),
  ],
  analysisSets: [
    ...(hierarchy?.analysisSets ?? []).map((as) => ({
      ...as,
      fromHierarchyId: hierarchy?.id,
    })),
    ...(parentTemplates?.analysisSets ?? [])
      .filter((pas) => !hierarchy?.analysisSets?.some((as) => as.name === pas.name))
      .map((pas) => ({
        ...pas,
        disableEditing: true,
      })),
  ],
  dataSubsets: [
    ...(hierarchy?.dataSubsets ?? []).map((ds) => ({
      ...ds,
      fromHierarchyId: hierarchy?.id,
    })),
    ...(parentTemplates?.dataSubsets ?? [])
      .filter((pds) => !hierarchy?.dataSubsets?.some((ds) => ds.name === pds.name))
      .map((pds) => ({
        ...pds,
        disableEditing: true,
      })),
  ],
  groupingFactors: [
    ...(hierarchy?.groupingFactors ?? []).map((gf) => ({
      ...gf,
      fromHierarchyId: hierarchy?.id,
    })),
    ...(parentTemplates?.groupingFactors ?? [])
      .filter((pgf) => !hierarchy?.groupingFactors?.some((gf) => gf.name === pgf.name))
      .map((pgf) => ({
        ...pgf,
        disableEditing: true,
      })),
  ],
});

/**
 * Props for the ArsContextInterface component.
 *
 * @interface ArsContextInterfaceProps
 */
export interface ArsContextInterfaceProps<T> {
  /**
   * Child elements to be rendered within the context provider.
   */
  children: React.ReactNode;
  editorDefns: EditorDefns<T>;
}

/**
 * Provides ARS context to child components based on the hierarchy context.
 *
 * @param props - The component props
 * @param props.children - The child components to render within the ARS context
 * @param props.editorDefns - Editor definitions for the ARS context
 * @returns The ArsContextInterface component wrapping its children with ARS context
 */
export const ArsContextInterface = <T,>({
  children,
  editorDefns,
}: ArsContextInterfaceProps<T>): React.ReactElement => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const tlfContext = useTlfContext();
  const hierarchyPayloadFromHierarchy = getHierarchyPayloadFromHierarchy(
    {
      id: hierarchyContext?.hierarchy?.id ?? CURRENT_HIERARCHY_ID,
      analysisMethods: hierarchyContext?.hierarchy?.analysisMethods,
      analysisSets: hierarchyContext?.hierarchy?.analysisSets,
      dataSubsets: hierarchyContext?.hierarchy?.dataSubsets,
      groupingFactors: hierarchyContext?.hierarchy?.groupingFactors,
    },
    hierarchyContext?.parentTemplates,
  );
  const clinicalLibraryData = hierarchyContext?.hierarchy
    ? getClinicalLibraryData(
        hierarchyContext.hierarchy,
        hierarchyContext.hierarchyNodes,
        appSettings.clinicalLibraries,
      )
    : undefined;

  /**
   * Update the hierarchy when the ARS is changed
   *
   * @param state - ArsState returned from the context
   */
  const handleArsUpdate = async (state: ArsContextState<T>) => {
    const hierarchyPayload = getHierarchyPayload(
      state,
      hierarchyContext?.hierarchy?.id ??
        // istanbul ignore next
        "",
    );

    const arsSaveBlockers = await getArsSaveBlockers(hierarchyPayload);

    hierarchyContext?.dispatch({
      operation: UPDATE_HIERARCHY,
      hierarchy: hierarchyPayload,
      arsSaveBlockers,
    });
  };

  /**
   * Current variable list for the hierarchy, including any parent templates
   */
  const variables =
    clinicalLibraryData?.clinicalDatasets.flatMap(datasetToAnnotatedVariables) ?? [];

  return !hierarchyContext?.hierarchy || !hierarchyContext.hierarchy.arsEnabled ? (
    <ArsContextProvider
      analyses={[]}
      analysisMethods={[]}
      analysisSets={[]}
      caseWhenConditions={[]}
      comparators={[]}
      dataSubsets={[]}
      disableAnalysisMethodEdit
      editorDefns={editorDefns}
      groupingFactors={[]}
      initialSidebarView={"groupingFactor"}
      variables={variables}
      updateARS={() => {}}
    >
      {children}
    </ArsContextProvider>
  ) : (
    <ArsContextProvider<T>
      analyses={[]}
      analysisMethods={hierarchyPayloadFromHierarchy.analysisMethods}
      analysisSets={hierarchyPayloadFromHierarchy.analysisSets}
      caseWhenConditions={getCaseWhenConditionsFromTlf(tlfContext.tlfData)}
      comparators={Object.values(ARS.ConditionComparatorEnum)}
      dataSubsets={hierarchyPayloadFromHierarchy.dataSubsets}
      disableAnalysisMethodEdit={
        !appSettings.applicationPermissions.includes(eApplicationPermission.EditAnalysisMethods)
      }
      editorDefns={editorDefns}
      groupingFactors={hierarchyPayloadFromHierarchy.groupingFactors}
      initialSidebarView={
        tlfContext.tlfData?.type === eTlfItemType.arsListing ? "variable" : "groupingFactor"
      }
      variables={variables}
      updateARS={handleArsUpdate}
    >
      {children}
    </ArsContextProvider>
  );
};

ArsContextInterface.displayName = "ArsContextInterface";
